import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';

import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { GridCellModes, GridCellModesModel, GridCellParams } from '@mui/x-data-grid-pro';

import StyledDataGrid from '~/components/CustomerCommunication/HeroPlanner/Components/ScheduleForms/StyledDataGrid';

import {
  useGetCabinUpgradePricing,
  useUpsertCabinUpgrades,
} from '~/hooks/apis/flights-charter/useCharterUpgradePricing';

import type { CabinUpgradeEligibilityUpsertItem, CabinUpgradePricingUpsertItem } from '~/services/CharterService';

import {
  EDITABLE_ELIGIBILITY_FIELDS,
  EDITABLE_PRICING_FIELDS,
  charterUpgradeColumnGroupingModel,
  charterUpgradeColumns,
} from './CharterUpgradePricingColumns';
import CharterUpgradePricingFilters from './CharterUpgradePricingFilters';
import {
  type UpgradePricingFilters,
  buildEligibilityPayload,
  buildPricingPayload,
  extractEligibilityChange,
  extractPricingChanges,
  pivotToFlightRows,
} from './CharterUpgradePricingHelpers';
import { type FlightUpgradeRow } from './CharterUpgradePricingTypes';

const ALL_EDITABLE_FIELDS = [...EDITABLE_ELIGIBILITY_FIELDS, ...EDITABLE_PRICING_FIELDS];
const editableCellSx = ALL_EDITABLE_FIELDS.map((f) => `& .MuiDataGrid-cell[data-field="${f}"]`).join(', ');

// Page

export default function CharterUpgradePricingPage() {
  const { enqueueSnackbar } = useSnackbar();

  const [appliedFilters, setUpgradePricingFilters] = useState<UpgradePricingFilters>({});
  const [isViewMode, setIsViewMode] = useState(true);
  const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({});
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);

  // Separate tracking for eligibility (per-flight) and pricing (global)
  const [editedEligibility, setEditedEligibility] = useState<Map<string, CabinUpgradeEligibilityUpsertItem>>(new Map());
  const [editedPricing, setEditedPricing] = useState<Map<string, CabinUpgradePricingUpsertItem>>(new Map());
  // Local row overrides for display (merged eligibility + pricing edits)
  const [editedRows, setEditedRows] = useState<Map<string, FlightUpgradeRow>>(new Map());

  const hasUnsavedChanges = editedEligibility.size > 0 || editedPricing.size > 0;

  // Keep refs in sync so Save can read an edit committed by the grid before React has re-rendered.
  const editedEligibilityRef = useRef(editedEligibility);
  editedEligibilityRef.current = editedEligibility;
  const editedPricingRef = useRef(editedPricing);
  editedPricingRef.current = editedPricing;
  const editedRowsRef = useRef(editedRows);
  editedRowsRef.current = editedRows;

  const pricingQuery = useGetCabinUpgradePricing({ ...appliedFilters, limit: 1000 });
  const upsertCabinUpgradesMutation = useUpsertCabinUpgrades();

  const baseRows = useMemo<FlightUpgradeRow[]>(() => {
    return pivotToFlightRows(pricingQuery.data?.result ?? []);
  }, [pricingQuery.data]);

  const rows = useMemo(() => baseRows.map((base) => editedRows.get(base.id) ?? base), [baseRows, editedRows]);

  const resetEdits = useCallback(() => {
    const nextEditedEligibility = new Map<string, CabinUpgradeEligibilityUpsertItem>();
    const nextEditedPricing = new Map<string, CabinUpgradePricingUpsertItem>();
    const nextEditedRows = new Map<string, FlightUpgradeRow>();

    editedEligibilityRef.current = nextEditedEligibility;
    editedPricingRef.current = nextEditedPricing;
    editedRowsRef.current = nextEditedRows;

    setEditedEligibility(nextEditedEligibility);
    setEditedPricing(nextEditedPricing);
    setEditedRows(nextEditedRows);
  }, []);

  const handleSearch = useCallback(
    (filters: UpgradePricingFilters) => {
      resetEdits();
      setUpgradePricingFilters(filters);
    },
    [resetEdits],
  );

  const handleClearFilters = useCallback(() => {
    resetEdits();
    setUpgradePricingFilters({});
  }, [resetEdits]);

  const processRowUpdate = useCallback((newRow: FlightUpgradeRow, oldRow: FlightUpgradeRow): FlightUpgradeRow => {
    // Track eligibility changes
    const eligibilityChange = extractEligibilityChange(newRow, oldRow);
    if (eligibilityChange) {
      const nextEditedEligibility = new Map(editedEligibilityRef.current).set(newRow.id, eligibilityChange);
      editedEligibilityRef.current = nextEditedEligibility;
      setEditedEligibility(nextEditedEligibility);
    }

    // Track pricing changes
    const pricingChanges = extractPricingChanges(newRow, oldRow);
    if (pricingChanges.size > 0) {
      const nextEditedPricing = new Map(editedPricingRef.current);
      pricingChanges.forEach((item, key) => nextEditedPricing.set(`${item.eligibilityId}_${key}`, item));
      editedPricingRef.current = nextEditedPricing;
      setEditedPricing(nextEditedPricing);
    }

    // Update display row
    const nextEditedRows = new Map(editedRowsRef.current).set(newRow.id, newRow);
    editedRowsRef.current = nextEditedRows;
    setEditedRows(nextEditedRows);
    return newRow;
  }, []);

  const handleCellClick = useCallback(
    (params: GridCellParams, event: React.MouseEvent) => {
      if (isViewMode || !params.isEditable) return;

      if ((event.target as HTMLElement).nodeType === 1 && !event.currentTarget.contains(event.target as Element)) {
        return;
      }

      setCellModesModel((prev) => ({
        ...Object.keys(prev).reduce(
          (acc, id) => ({
            ...acc,
            [id]: Object.keys(prev[id]).reduce(
              (acc2, field) => ({ ...acc2, [field]: { mode: GridCellModes.View } }),
              {},
            ),
          }),
          {},
        ),
        [params.id]: {
          ...Object.keys(prev[params.id] || {}).reduce(
            (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
            {},
          ),
          [params.field]: { mode: GridCellModes.Edit },
        },
      }));
    },
    [isViewMode],
  );

  const handleSave = useCallback(async () => {
    const currentEditedEligibility = editedEligibilityRef.current;
    const currentEditedPricing = editedPricingRef.current;

    if (currentEditedEligibility.size === 0 && currentEditedPricing.size === 0) {
      enqueueSnackbar('No changes to save', { variant: 'warning' });
      return;
    }

    const eligibilityPayload = buildEligibilityPayload(currentEditedEligibility);
    const pricingPayload = buildPricingPayload(currentEditedPricing);

    try {
      await upsertCabinUpgradesMutation.mutateAsync({ eligibility: eligibilityPayload, pricing: pricingPayload });
      enqueueSnackbar('Changes saved', { variant: 'success' });
      resetEdits();
      setCellModesModel({});
      setIsViewMode(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      enqueueSnackbar(`Failed to save: ${message}`, { variant: 'error' });
    }
  }, [upsertCabinUpgradesMutation, enqueueSnackbar, resetEdits]);

  const handleExitEditMode = useCallback(() => {
    if (hasUnsavedChanges) {
      setIsDiscardDialogOpen(true);
    } else {
      setIsViewMode(true);
    }
  }, [hasUnsavedChanges]);

  const handleConfirmDiscard = useCallback(() => {
    resetEdits();
    setCellModesModel({});
    setIsDiscardDialogOpen(false);
    setIsViewMode(true);
  }, [resetEdits]);

  const isSaving = upsertCabinUpgradesMutation.isPending;
  const isLoading = pricingQuery.isFetching;

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="h4" fontWeight={600}>
          Charter Cabin Upgrades
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configure day windows (per-flight) and pricing (per flight) for cabin upgrades.
        </Typography>
      </Stack>

      <CharterUpgradePricingFilters isLoading={isLoading} onSearch={handleSearch} onClear={handleClearFilters} />

      {pricingQuery.isError && <Alert severity="error">Failed to load upgrade pricing data</Alert>}

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          {rows.length > 0 && `${rows.length} flight${rows.length !== 1 ? 's' : ''} (9 upgrade combos each)`}
        </Typography>

        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            size="small"
            onClick={isViewMode ? () => setIsViewMode(false) : handleExitEditMode}
          >
            {isViewMode ? 'Edit' : 'View Mode'}
          </Button>

          {!isViewMode && (
            <LoadingButton
              variant="contained"
              size="small"
              color="primary"
              onClick={handleSave}
              loading={isSaving}
              disabled={isSaving}
            >
              Save Changes
            </LoadingButton>
          )}
        </Stack>
      </Stack>

      {isLoading && (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!isLoading && !pricingQuery.isError && rows.length === 0 && (
        <Alert severity="info">
          No flights found. Use the filters above to find flights, then configure upgrade day windows and pricing.
        </Alert>
      )}

      {!isLoading && (pricingQuery.isError || rows.length > 0) && (
        <Box sx={{ width: '100%' }}>
          <StyledDataGrid
            rows={rows}
            columns={charterUpgradeColumns}
            columnGroupingModel={charterUpgradeColumnGroupingModel}
            editMode="cell"
            isCellEditable={() => !isViewMode}
            cellModesModel={cellModesModel}
            onCellModesModelChange={setCellModesModel}
            onCellClick={handleCellClick}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={(err) =>
              enqueueSnackbar(err instanceof Error ? err.message : 'Invalid value', {
                variant: 'error',
              })
            }
            disableRowSelectionOnClick
            sx={{
              maxHeight: 800,
              ...(!isViewMode && {
                [`${editableCellSx}`]: {
                  outline: '1px solid',
                  outlineColor: 'grey.400',
                  outlineOffset: -1,
                  borderRadius: 1,
                  '&:hover': { outlineColor: 'primary.main' },
                },
                '& .MuiDataGrid-cell--editing': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: -2,
                  boxShadow: 1,
                },
              }),
            }}
          />
        </Box>
      )}

      {/* Discard confirmation */}
      <Dialog open={isDiscardDialogOpen} onClose={() => setIsDiscardDialogOpen(false)}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>You have unsaved changes. If you leave Edit Mode, they will be discarded.</DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDiscard} variant="text">
            Discard changes
          </Button>
          <Button onClick={() => setIsDiscardDialogOpen(false)} variant="contained" color="primary">
            Keep editing
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
